import { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { format, addMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  Grid as MuiGrid,
  Button as MuiButton,
  Dialog as MuiDialog,
  Backdrop as MuiBackdrop,
  TextField as MuiTextField,
  Typography as MuiTypography,
  DialogTitle as MuiDialogTitle,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  CircularProgress as MuiCircularProgress,
} from "@mui/material";
import { Done as MuiDoneIcon } from "@mui/icons-material";
import { useAlerts } from "common";
import { useTransfersContext } from "app/services/transfersContext";
import { postUploadTransfers } from "app/services/transfersService";
import { DropZone } from "app/shared/ui/DropZone/DropZone";
import { ConfirmationDialog } from "./ConfirmationDialog";

export const UploadTransfers = ({
  openDialog,
  setOpenDialog,
  glPeriod,
  location,
}) => {
  const [open, setOpen] = useState(openDialog);
  const [loading, setLoading] = useState(false);
  const [uploadDocumentError, setUploadDocumentError] = useState(null);
  const [uploadedDocument, setUploadedDocument] = useState("");
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const { t } = useTranslation();

  const { setAlert, clearAlert } = useAlerts();

  const uploadTranfersDialogStr = "UploadedTransfers.uploadTransfersDialog";

  const allowedMimeTypes = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-excel": [".xls"],
  };
  const formData = {
    file: "",
  };

  const handleClose = () => {
    setOpen(false);
    setOpenDialog(false);
  };

  const { lastUploadedInfo, setFilters, setLastUploadedInfo } =
    useTransfersContext();

  const uploadTransfers = () => {
    postUploadTransfers(
      uploadedDocument,
      setLoading,
      setUploadDocumentError,
      setLastUploadedInfo,
      handleClose,
      setAlert,
      clearAlert,
      setFilters
    );
  };

  const getFormattedFileName = (file) => {
    return (
      file.name.split(".")[0].substring(0, 40) +
      (file.name.length > 40 ? "... ." : ".") +
      file.name.split(".").pop()
    );
  };

  const getNextGLPeriod = (glPeriod) => {
    return glPeriod
      ? format(addMonths(new Date(glPeriod), 1), "LLL-yyyy").toUpperCase()
      : "";
  };

  useEffect(() => {
    setOpen(openDialog);
    clearAlert();
    // eslint-disable-next-line
  }, [openDialog]);

  const handleCloseConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  };

  return (
    <Formik
      initialValues={formData}
      enableReinitialize
      validationSchema={uploadTransfersValidation}
    >
      {(props) => {
        const { handleSubmit, setFieldValue, dirty, isValid } = props;
        // Document upload handler
        const handleDocumentUpload = (files) => {
          const file = Array.isArray(files) ? files[0] : files?.[0];

          if (file instanceof File) {
            setUploadedDocument(file);
            setFieldValue("file", file);
            return;
          }

          setUploadedDocument("");
          setFieldValue("file", "");
        };
        return (
          <>
            <form onSubmit={handleSubmit}>
              <MuiDialog open={open} fullWidth maxWidth="sm">
                <MuiDialogTitle>
                  {t(`${uploadTranfersDialogStr}.title`)}
                </MuiDialogTitle>
                <MuiDialogContent>
                  <MuiGrid size={12}
                    container
                    spacing={2}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <MuiGrid size={6}>
                      <MuiTextField
                        label={`${t(
                          `${uploadTranfersDialogStr}.fields.glPeriod.label`
                        )}`}
                        value={getNextGLPeriod(glPeriod)}
                        readOnly
                        fullWidth
                        variant="standard"
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      />
                    </MuiGrid>
                    <MuiGrid size={6} sx={{ textAlign: "right" }}>
                      <MuiButton
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setShowConfirmationDialog(true);
                        }}
                        disabled={!lastUploadedInfo}
                      >
                        {t(
                          `${uploadTranfersDialogStr}.advanceGLPeriod.ctaButtonText`
                        )}
                      </MuiButton>
                    </MuiGrid>
                    <MuiGrid size={12}>
                      <MuiBackdrop open={loading} invisible sx={{ zIndex: 99 }}>
                        <MuiCircularProgress />
                      </MuiBackdrop>
                      <DropZone
                        acceptedFiles={allowedMimeTypes}
                        dropzoneText={t(
                          `${uploadTranfersDialogStr}.fields.transferUploader.label`
                        )}
                        handleDocumentUpload={handleDocumentUpload}
                      />
                    </MuiGrid>
                    <MuiGrid size={12}>
                      {uploadDocumentError && (
                        <MuiTypography color="error" variant="body2">
                          {`${getFormattedFileName(
                            uploadedDocument
                          )} ${uploadDocumentError}`}
                        </MuiTypography>
                      )}
                      {!uploadDocumentError && uploadedDocument?.name && (
                        <MuiTypography
                          variant="body2"
                          sx={{
                            display: "block",
                          }}
                        >
                          <MuiDoneIcon
                            size="small"
                            sx={{ verticalAlign: "text-bottom" }}
                          />{" "}
                          {getFormattedFileName(uploadedDocument)}
                        </MuiTypography>
                      )}
                    </MuiGrid>
                  </MuiGrid>
                </MuiDialogContent>
                <MuiDialogActions>
                  <MuiButton onClick={handleClose} color="primary">
                    {t(`${uploadTranfersDialogStr}.actionButtons.cancel.label`)}
                  </MuiButton>
                  <MuiButton
                    color="primary"
                    onClick={() => {
                      uploadTransfers();
                    }}
                    disabled={!dirty || !isValid}
                  >
                    {t(`${uploadTranfersDialogStr}.actionButtons.upload.label`)}
                  </MuiButton>
                </MuiDialogActions>
              </MuiDialog>
            </form>
            {
              <ConfirmationDialog
                open={showConfirmationDialog}
                setShowConfirmationDialog={setShowConfirmationDialog}
                setOpenUploadTransfersDialog={setOpenDialog}
                handleClose={handleCloseConfirmationDialog}
                advanceGLPeriod={getNextGLPeriod(glPeriod)}
                location={location}
              />
            }
          </>
        );
      }}
    </Formik>
  );
};

const uploadTransfersValidation = Yup.object().shape({
  file: Yup.mixed().required(),
});
